import React, { useState } from 'react';
import { Calendar, Clock, CheckCircle2, XCircle, AlertTriangle, Play, RefreshCw } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Button,
  Select,
  SelectOption,
} from '../ui';
import { Checkbox } from '../ui/checkbox';
import type { Employee } from '../../hooks/useEmployees';
import type { GenerateTimeRecordsResult } from '../../hooks/useTimeRecords';

interface PaginatedEmployees {
  data: Employee[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface GenerateTimeRecordsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGenerate: (params: {
    start_date: string;
    end_date: string;
    employee_id?: string;
    overwrite?: boolean;
  }) => void;
  isLoading: boolean;
  result: GenerateTimeRecordsResult | null;
  error: Error | null;
  employees?: PaginatedEmployees;
}

export function GenerateTimeRecordsDialog({
  open,
  onOpenChange,
  onGenerate,
  isLoading,
  result,
  error,
  employees,
}: GenerateTimeRecordsDialogProps) {
  // Default: yesterday
  const getYesterday = () => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    return date.toISOString().split('T')[0];
  };

  const getToday = () => {
    return new Date().toISOString().split('T')[0];
  };

  const [startDate, setStartDate] = useState(getYesterday());
  const [endDate, setEndDate] = useState(getYesterday());
  const [selectedEmployee, setSelectedEmployee] = useState<string>('');
  const [overwrite, setOverwrite] = useState(false);

  const handleGenerate = () => {
    onGenerate({
      start_date: startDate,
      end_date: endDate,
      employee_id: selectedEmployee || undefined,
      overwrite,
    });
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset state after closing
    setTimeout(() => {
      setStartDate(getYesterday());
      setEndDate(getYesterday());
      setSelectedEmployee('');
      setOverwrite(false);
    }, 200);
  };

  // Calculate date range in days
  const getDateRangeDays = () => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  const isValidDateRange = () => {
    if (!startDate || !endDate) return false;
    const start = new Date(startDate);
    const end = new Date(endDate);
    return start <= end && getDateRangeDays() <= 366;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Play className="h-5 w-5" />
            Генерация записей учёта времени
          </DialogTitle>
          <DialogDescription>
            Ручной запуск генерации time-records из событий посещаемости
          </DialogDescription>
        </DialogHeader>

        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-12">
            <Clock className="h-16 w-16 animate-spin text-blue-500 mb-4" />
            <p className="text-lg font-medium">Генерация записей...</p>
            <p className="text-sm text-muted-foreground mt-2">
              Обработка событий посещаемости
            </p>
          </div>
        )}

        {/* Success State */}
        {!isLoading && result && !error && (
          <div className="space-y-4 py-4">
            <div className="flex justify-center">
              <div className="rounded-full bg-green-100 p-4">
                <CheckCircle2 className="h-12 w-12 text-green-600" />
              </div>
            </div>

            <div className="text-center">
              <h3 className="text-xl font-bold text-green-600 mb-2">
                Генерация завершена
              </h3>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="rounded-lg border bg-blue-50 p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{result.generated_count}</p>
                <p className="text-sm text-blue-800">Создано</p>
              </div>
              <div className="rounded-lg border bg-orange-50 p-4 text-center">
                <p className="text-2xl font-bold text-orange-600">{result.updated_count}</p>
                <p className="text-sm text-orange-800">Обновлено</p>
              </div>
              <div className="rounded-lg border bg-purple-50 p-4 text-center">
                <p className="text-2xl font-bold text-purple-600">{result.employees_processed}</p>
                <p className="text-sm text-purple-800">Сотрудников</p>
              </div>
            </div>

            {result.errors && result.errors.length > 0 && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">
                      Предупреждения ({result.errors.length}):
                    </p>
                    <ul className="mt-1 text-sm text-yellow-700 list-disc list-inside">
                      {result.errors.slice(0, 5).map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                      {result.errors.length > 5 && (
                        <li>...и ещё {result.errors.length - 5}</li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button onClick={handleClose}>
                Закрыть
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Error State */}
        {!isLoading && error && (
          <div className="space-y-4 py-4">
            <div className="flex justify-center">
              <div className="rounded-full bg-red-100 p-4">
                <XCircle className="h-12 w-12 text-red-600" />
              </div>
            </div>

            <div className="text-center">
              <h3 className="text-xl font-bold text-red-600 mb-2">
                Ошибка генерации
              </h3>
            </div>

            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <p className="text-sm text-red-800">
                {error.message || 'Произошла неизвестная ошибка'}
              </p>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Закрыть
              </Button>
              <Button onClick={handleGenerate}>
                Повторить
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Form State (initial) */}
        {!isLoading && !result && !error && (
          <>
            <div className="space-y-4 py-4">
              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Дата начала</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="date"
                      value={startDate}
                      max={getToday()}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Дата окончания</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="date"
                      value={endDate}
                      max={getToday()}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>

              {/* Employee Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Сотрудник (опционально)</label>
                <Select
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                >
                  <SelectOption value="">Все сотрудники</SelectOption>
                  {employees?.data?.map((emp) => (
                    <SelectOption key={emp.id} value={emp.id}>
                      {emp.last_name} {emp.first_name} ({emp.employee_number || 'N/A'})
                    </SelectOption>
                  ))}
                </Select>
                <p className="text-xs text-muted-foreground">
                  Оставьте пустым для генерации по всем сотрудникам
                </p>
              </div>

              {/* Overwrite Option */}
              <div className="flex items-center space-x-3 rounded-lg border p-4">
                <Checkbox
                  id="overwrite"
                  checked={overwrite}
                  onCheckedChange={(checked) => setOverwrite(checked === true)}
                />
                <div className="space-y-1">
                  <label
                    htmlFor="overwrite"
                    className="text-sm font-medium leading-none cursor-pointer flex items-center gap-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Перезаписать существующие записи
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Если включено, существующие записи будут пересчитаны и обновлены
                  </p>
                </div>
              </div>

              {/* Info Box */}
              <div className="rounded-lg border bg-blue-50 p-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-900">
                    <p className="font-medium">Информация:</p>
                    <ul className="mt-1 list-disc list-inside text-blue-800">
                      <li>Период: {getDateRangeDays()} {getDateRangeDays() === 1 ? 'день' : 'дней'}</li>
                      <li>{overwrite ? 'Существующие записи будут перезаписаны' : 'Существующие записи будут пропущены'}</li>
                      <li>Максимальный период: 366 дней</li>
                    </ul>
                  </div>
                </div>
              </div>

              {!isValidDateRange() && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                  <p className="text-sm text-red-800">
                    {!startDate || !endDate
                      ? 'Укажите даты начала и окончания'
                      : new Date(startDate) > new Date(endDate)
                        ? 'Дата начала должна быть раньше даты окончания'
                        : 'Максимальный период - 366 дней'}
                  </p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Отмена
              </Button>
              <Button
                onClick={handleGenerate}
                disabled={!isValidDateRange()}
              >
                <Play className="h-4 w-4 mr-2" />
                Запустить генерацию
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
